/**
 * Snapshot serialisation for the header animation's filter state.
 *
 * Kept in its own module for two reasons: it is pure, so it can be unit tested
 * without a DOM, and the animation's entry file must not export anything — an
 * entry with exports bundles as an ES module, and it is loaded as a classic
 * script, so `export` becomes a runtime parse error.
 */

/** The filter state that survives a navigation. */
export interface PfState {
  px: Float32Array
  pw: Float32Array
  truth: Float32Array
  est: Float32Array
  lo: Float32Array
  hi: Float32Array
  sx: Float32Array
  sy: Float32Array
  sz: Float32Array
  tx: number
  ty: number
  tz: number
  head: number
  count: number
  /**
   * Running average of the sum of squared normalised weights.
   *
   * Carried rather than recomputed because `pw` is normalised to each column's own
   * maximum for drawing, which discards the scale this needs, and because it is an
   * average over many past steps that no single column holds.
   */
  sumSq: number
}

const SCALAR_BYTES = 24 // tx, ty, tz, head, count, sumSq as Float32
const SCALARS = SCALAR_BYTES / 4

/** Byte length a snapshot of this state must have. */
function snapshotBytes(s: PfState): number {
  return (
    SCALAR_BYTES +
    s.px.byteLength +
    s.pw.byteLength +
    s.truth.byteLength +
    s.est.byteLength +
    s.lo.byteLength +
    s.hi.byteLength +
    s.sx.byteLength +
    s.sy.byteLength +
    s.sz.byteLength
  )
}

/** Pack state into one base64 string. Pure, so it is unit tested. */
export function packState(s: PfState): string {
  const scalars = new Float32Array([s.tx, s.ty, s.tz, s.head, s.count, s.sumSq])
  const blob = new Uint8Array(snapshotBytes(s))
  let at = 0
  for (const part of [scalars, s.px, s.pw, s.truth, s.est, s.lo, s.hi, s.sx, s.sy, s.sz]) {
    blob.set(new Uint8Array(part.buffer, part.byteOffset, part.byteLength), at)
    at += part.byteLength
  }
  let binary = ''
  for (let i = 0; i < blob.length; i++) binary += String.fromCharCode(blob[i]!)
  return btoa(binary)
}

/**
 * Unpack into `s` in place. Returns false and leaves `s` untouched for anything
 * that is not a snapshot of exactly the right size — a truncated value, garbage,
 * or a snapshot written when STEPS or PARTICLES differed.
 */
export function unpackState(raw: string, s: PfState): boolean {
  try {
    const binary = atob(raw)
    if (binary.length !== snapshotBytes(s)) return false
    const blob = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) blob[i] = binary.charCodeAt(i)
    const scalars = new Float32Array(blob.buffer, 0, SCALARS)
    let at = SCALAR_BYTES
    for (const part of [s.px, s.pw, s.truth, s.est, s.lo, s.hi, s.sx, s.sy, s.sz]) {
      part.set(new Float32Array(blob.buffer, at, part.length))
      at += part.byteLength
    }
    s.tx = scalars[0]!
    s.ty = scalars[1]!
    s.tz = scalars[2]!
    s.head = scalars[3]!
    s.count = scalars[4]!
    s.sumSq = scalars[5]!
    return true
  } catch {
    return false
  }
}
